// ABOUTME: Shared controller hook for the AI assistant chat surface. Owns the
// conversation, streaming, voice, attachment, approval, and scheduling logic
// so the side panel (ChatPanel) and the full-screen /chat page (ChatPage)
// share one implementation via ChatSurface.

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useChatReady } from '@/components/chat/useChatReady'
import { useQueryClient } from '@tanstack/react-query'
import { useUiStore } from '@/stores/useUiStore'
import { clearChatHistory } from '@/api/agent'
import { AGENT_RUN_ACTIVITY_KEY } from '@/api/agentRuns'
import { useAIChatStore, selectActiveConversation } from '@/stores/useAIChatStore'
import { useBillingStore } from '@/stores/useBillingStore'
import { useDraftsStore } from '@/stores/useDraftsStore'
import { isApiError } from '@/api/client'
import { useConnectedAccountsStore } from '@/stores/useConnectedAccountsStore'
import { publishNowReply } from '@/components/chat/publishReply'
import {
  useVoiceRecorder,
  type VoiceRecorderError,
  type VoiceNoteResult,
} from '@/hooks/useVoiceRecorder'
import { useHermesChat } from '@/hooks/useHermesChat'
import { usePublishConfirmationStream } from '@/hooks/usePublishConfirmationStream'
import { useImageUpload } from '@/hooks/useImageUpload'
import { toContextRefs, useAskAbout } from '@/lib/askAbout'
import type { AttachmentRef } from '@/types'

export function useChatSurface() {
  useChatReady()
  const { askAbout, clear: clearAskAbout } = useAskAbout()

  // AI Chat store
  const activeConversation = useAIChatStore(selectActiveConversation)
  const createConversation = useAIChatStore((state) => state.createConversation)
  const addMessage = useAIChatStore((state) => state.addMessage)
  const updateMessage = useAIChatStore((state) => state.updateMessage)
  const clearConversation = useAIChatStore((state) => state.clearConversation)
  // The one shared balance (#2191): same store value the top-bar indicator
  // renders, refreshed by AppShell's mount fetch and by the post-turn
  // credits_remaining SSE events. No chat-local copy.
  const creditsRemaining = useBillingStore((state) => state.creditBalance?.balance ?? null)

  const [inputValue, setInputValue] = useState('')
  const [voiceRecorderError, setVoiceRecorderError] = useState<VoiceRecorderError | null>(null)
  const [annotationImageUrl, setAnnotationImageUrl] = useState<string | null>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const { pendingCount, hasFailedMessage, send, retryLastMessage } = useHermesChat()

  // Voice note: record -> upload -> server-side ElevenLabs transcription ->
  // post the transcript as a normal message with the audio attached (#2609).
  // Replaces the old browser-native Web Speech API dictation entirely — one
  // reliable behavior instead of two live voice paths.
  // A recorded note waits in the composer until the owner sends or deletes it.
  const [voiceNote, setVoiceNote] = useState<VoiceNoteResult | null>(null)
  const discardVoiceNote = useCallback(() => setVoiceNote(null), [])

  const sendVoiceNote = useCallback(() => {
    if (!activeConversation || !voiceNote) return
    const { transcript, attachment } = voiceNote
    setVoiceNote(null)
    const outgoing = transcript.trim() || '(voice note)'
    const messageAttachments = [
      {
        url: attachment.url,
        filename: attachment.filename,
        content_type: attachment.mime_type,
        size_bytes: attachment.size_bytes,
        upload_id: attachment.upload_id,
      },
    ]
    const clientMessageId = addMessage(activeConversation.id, {
      role: 'user',
      content: outgoing,
      attachments: messageAttachments,
    })
    send(outgoing, activeConversation.id, clientMessageId, addMessage, updateMessage, [attachment])
  }, [activeConversation, voiceNote, addMessage, send, updateMessage])

  const {
    status: voiceRecorderStatus,
    isRecording,
    isSupported: isVoiceRecorderSupported,
    startRecording: startVoiceRecording,
    stopRecording: stopVoiceRecording,
  } = useVoiceRecorder({
    onResult: setVoiceNote,
    onError: (error) => {
      setVoiceRecorderError(error)
      setTimeout(() => setVoiceRecorderError(null), 5000)
    },
  })

  // Image upload hook
  const {
    pendingFiles,
    compressingCount,
    fileInputRef,
    addFiles,
    removeFile,
    uploadAll,
    clear: clearFiles,
    openFilePicker,
  } = useImageUpload()

  // Create initial conversation if none exists
  useEffect(() => {
    if (!activeConversation) {
      createConversation()
    }
  }, [activeConversation, createConversation])

  // Get messages from active conversation
  const messages = useMemo(() => activeConversation?.messages || [], [activeConversation?.messages])

  // Publish-confirmation cards. Agent replies arrive via the shared
  // useEventStream -> useHermesReplyStore path inside useHermesChat.
  usePublishConfirmationStream({
    conversationId: activeConversation?.id,
    addMessage,
  })

  // Auto-scroll to bottom when new messages arrive.
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]'
      )
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight
      }
    }
  }, [messages])

  // Focus input on mount.
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text)
  }, [])

  // Resend a specific user bubble that never reached the server (#2281) —
  // offline at send time, or the transport rejected outright. Resends INTO
  // the same message id rather than appending a new one, so a retry doesn't
  // duplicate the turn in the transcript.
  const handleRetryMessage = useCallback(
    (messageId: string) => {
      if (!activeConversation) return
      const message = activeConversation.messages.find((m) => m.id === messageId)
      if (!message || message.role !== 'user') return
      const attachmentRefs = message.attachments?.map((a) => ({
        upload_id: a.upload_id ?? '',
        url: a.url,
        mime_type: a.content_type,
        filename: a.filename,
        size_bytes: a.size_bytes,
        ...(a.file_id ? { file_id: a.file_id } : {}),
      }))
      updateMessage(activeConversation.id, messageId, { status: 'pending' })
      send(
        message.content,
        activeConversation.id,
        messageId,
        addMessage,
        updateMessage,
        attachmentRefs && attachmentRefs.length > 0 ? attachmentRefs : undefined
      )
    },
    [activeConversation, addMessage, send, updateMessage]
  )

  // Offline banner + auto-retry-on-reconnect (#2281), modeled on
  // useMediaUploadStore's `online` listener. Only the OLDEST failed message is
  // retried automatically; any further failed message still needs its own
  // retry tap once this one settles.
  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== 'undefined' && !navigator.onLine
  )
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false)
      const conversation = selectActiveConversation(useAIChatStore.getState())
      const firstFailed = conversation?.messages.find(
        (m) => m.role === 'user' && m.status === 'failed'
      )
      if (firstFailed) handleRetryMessage(firstFailed.id)
    }
    const handleOffline = () => setIsOffline(true)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [handleRetryMessage])

  /** Post one owner turn: the bubble first, then the send. */
  const sendTurn = useCallback(
    (
      text: string,
      attachmentRefs: AttachmentRef[],
      contextRefs?: ReturnType<typeof toContextRefs>
    ) => {
      const conversation = selectActiveConversation(useAIChatStore.getState())
      if (!conversation) return
      // Placeholder for an attachment-only message — name what was actually sent
      // rather than always claiming "(image)".
      const allAre = (test: (a: AttachmentRef) => boolean) =>
        attachmentRefs.length > 0 && attachmentRefs.every(test)
      const outgoing =
        text ||
        (allAre((a) => !!a.file_id)
          ? '(document)'
          : allAre((a) => a.mime_type.startsWith('video/'))
            ? '(video)'
            : allAre((a) => a.mime_type.startsWith('audio/'))
              ? '(recording)'
              : allAre((a) => a.mime_type.startsWith('image/'))
                ? '(image)'
                : '(attachments)')
      const messageAttachments = attachmentRefs.map((a) => ({
        url: a.url,
        filename: a.filename,
        content_type: a.mime_type,
        size_bytes: a.size_bytes,
        upload_id: a.upload_id,
        ...(a.file_id ? { file_id: a.file_id } : {}),
      }))
      const clientMessageId = addMessage(conversation.id, {
        role: 'user',
        content: outgoing,
        attachments: messageAttachments.length > 0 ? messageAttachments : undefined,
      })
      send(
        outgoing,
        conversation.id,
        clientMessageId,
        addMessage,
        updateMessage,
        attachmentRefs.length > 0 ? attachmentRefs : undefined,
        contextRefs
      )
    },
    [addMessage, send, updateMessage]
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const hasText = inputValue.trim().length > 0
    const hasFiles = pendingFiles.length > 0
    if ((!hasText && !hasFiles) || !activeConversation) return
    // The send button is disabled mid-compression, but Enter bypasses it — a
    // photo still encoding is not yet in pendingFiles and would be dropped.
    if (compressingCount > 0) return

    const userMessage = inputValue.trim()
    const contextRefs = toContextRefs(askAbout)
    if (askAbout) clearAskAbout() // the reference goes with this message only
    const attachmentRefs = hasFiles ? await uploadAll() : []
    // A file that didn't upload keeps the message in the composer: it is never sent without it.
    if (attachmentRefs === null) return
    setInputValue('')
    clearFiles()
    sendTurn(userMessage, attachmentRefs, contextRefs)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  // The surface component stays mounted across conversation boundaries, so
  // hook-local composer state (staged attachments, the annotation editor, the
  // typed draft) survives them unless reset here — attachments staged for the
  // old conversation would otherwise upload into the new one (#1993). Typed
  // input is kept on *switch* only: the user may be hopping conversations to
  // check something mid-sentence.
  const resetComposerEphemera = (keepInput = false) => {
    clearFiles()
    setVoiceNote(null)
    setAnnotationImageUrl(null)
    if (!keepInput) setInputValue('')
  }

  const queryClient = useQueryClient()
  const handleClearConversation = async () => {
    try {
      await clearChatHistory()
    } catch (err) {
      useUiStore.getState().showToast({ type: 'error', title: (err as Error).message })
      return
    }
    resetComposerEphemera()
    clearConversation()
    void queryClient.invalidateQueries({ queryKey: [AGENT_RUN_ACTIVITY_KEY] })
    void queryClient.invalidateQueries({ queryKey: ['past-chats'] })
  }

  // The scheduling widget lives on message metadata (#2201) — under the turn
  // that produced it, like every other interactive card — so resolving or
  // dismissing it edits that one message rather than a store-global field.
  const removeSchedulingCard = useCallback(
    (messageId: string) => {
      const conversation = selectActiveConversation(useAIChatStore.getState())
      const metadata = conversation?.messages.find((m) => m.id === messageId)?.metadata
      if (!conversation || !metadata) return
      const rest = { ...metadata }
      delete rest.scheduling_card
      updateMessage(conversation.id, messageId, { metadata: rest })
    },
    [updateMessage]
  )

  const handleScheduleResolve = useCallback(
    // SchedulingCard's onSchedule contract passes the selected profiles and any
    // per-platform overrides, but the draft flow has never consumed either: the
    // draft row already carries its platforms and body, and scheduleDraft /
    // publishDraft key off the draft id alone. Only the retired image-campaign
    // branch forwarded them, to /agent/schedule-asset.
    async (messageId: string, scheduledAt: string | null) => {
      const conversation = selectActiveConversation(useAIChatStore.getState())
      const card = conversation?.messages.find((m) => m.id === messageId)?.metadata?.scheduling_card
      if (!conversation || !card) return

      // Universal draft flow — the only flow. The card emits scheduledAt=null
      // when the user picks "Now", which means publish IMMEDIATELY through the
      // real publish pipeline (POST /content/drafts/{id}/publish), not
      // schedule@now (which the backend rejects — scheduling requires a >=1
      // minute future time).
      //
      // The `kind: 'image'` branch that POSTed /agent/schedule-asset is gone
      // with the `schedule_and_publish` event that was its only trigger (#1924).
      try {
        if (scheduledAt) {
          await useDraftsStore.getState().scheduleDraft(card.draft_id, scheduledAt)
          const when = new Date(scheduledAt).toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
          })
          addMessage(conversation.id, {
            role: 'assistant',
            content: `Scheduled for ${when}.`,
          })
        } else {
          // The reply mirrors the server's decision (#2460): inside the grace
          // window the draft lands SCHEDULED and cancelable, and saying
          // "Publishing now." would hide the user's ~minutes-long cancel
          // window.
          const result = await useDraftsStore.getState().publishDraft(card.draft_id)
          addMessage(conversation.id, {
            role: 'assistant',
            content: publishNowReply(result),
          })
        }
      } catch (error) {
        const action = scheduledAt ? 'schedule' : 'publish'
        addMessage(conversation.id, {
          role: 'assistant',
          content: isApiError(error)
            ? `Failed to ${action} draft: ${error.message}`
            : `Failed to ${action} draft.`,
          isError: true,
        })
      }
      removeSchedulingCard(messageId)
    },
    [addMessage, removeSchedulingCard]
  )

  const handleScheduleDismiss = useCallback(
    (messageId: string) => {
      removeSchedulingCard(messageId)
    },
    [removeSchedulingCard]
  )

  const handleEditImage = useCallback((imageUrl: string) => {
    setAnnotationImageUrl(imageUrl)
  }, [])

  const handleEditComplete = useCallback(
    (assetId: string) => {
      if (!activeConversation) return
      // Add a new assistant message showing the edited image
      addMessage(activeConversation.id, {
        role: 'assistant',
        content: 'Here is your edited image:',
        metadata: { generated_image_asset_id: assetId },
      })
    },
    [activeConversation, addMessage]
  )

  // Inline draft actions surfaced from a DraftPreview card. Edit is a stub
  // until the inline editor ships; Schedule attaches a scheduling_card to the
  // same message so handleScheduleResolve performs the PATCH under that turn;
  // Delete permanently hard-deletes the draft via the pipeline store.
  const handleDraftEdit = useCallback(
    (draftId: string) => {
      if (!activeConversation) return
      console.debug('handleDraftEdit (stub)', draftId)
      addMessage(activeConversation.id, {
        role: 'assistant',
        content: 'Inline draft editing is coming soon. Open the pipeline to edit for now.',
      })
    },
    [activeConversation, addMessage]
  )

  const handleDraftSchedule = useCallback(
    async (draftId: string, messageId: string) => {
      const accountsStore = useConnectedAccountsStore.getState()
      if (accountsStore.accounts.length === 0 && !accountsStore.isLoading) {
        await accountsStore.fetch()
      }
      const accounts = useConnectedAccountsStore.getState().accounts
      const profiles = accounts.map((a) => ({
        id: a.id,
        name: a.account_name ?? a.platform,
        platform: a.platform,
        handle: a.account_name ?? a.platform,
      }))
      // Look up the draft so the strict 'draft' union has its required fields.
      const draft = useDraftsStore.getState().drafts.find((d) => d.id === draftId)
      const platforms = draft?.platforms ?? []
      const mediaUrl = draft?.media_urls?.[0]
      // Re-read the conversation AFTER the awaits above — the closure copy can
      // be stale by the time the accounts fetch lands.
      const conversation = selectActiveConversation(useAIChatStore.getState())
      const metadata = conversation?.messages.find((m) => m.id === messageId)?.metadata
      if (!conversation || !metadata) return
      updateMessage(conversation.id, messageId, {
        metadata: {
          ...metadata,
          scheduling_card: {
            kind: 'draft',
            draft_id: draftId,
            content_type: draft?.type ?? undefined,
            format: null,
            media_url: mediaUrl,
            platforms,
            profiles,
            no_accounts: profiles.length === 0,
          },
        },
      })
    },
    [updateMessage]
  )

  const handleDraftDelete = useCallback(async (draftId: string) => {
    try {
      await useDraftsStore.getState().deleteDraft(draftId)
    } catch {
      // Pipeline store records its own error; silently dismiss here.
    }
  }, [])

  return {
    // conversation state
    activeConversation,
    messages,
    creditsRemaining,
    isOffline,
    // streaming
    pendingCount,
    hasFailedMessage,
    retryLastMessage,
    handleRetryMessage,
    // task panel
    handleClearConversation,
    // input
    inputValue,
    setInputValue,
    handleSubmit,
    handleKeyDown,
    sendTurn,
    inputRef,
    scrollAreaRef,
    // voice
    isRecording,
    voiceRecorderStatus,
    voiceRecorderError,
    isVoiceRecorderSupported,
    startVoiceRecording,
    stopVoiceRecording,
    voiceNote,
    sendVoiceNote,
    discardVoiceNote,
    // attachments
    pendingFiles,
    compressingCount,
    addFiles,
    removeFile,
    openFilePicker,
    fileInputRef,
    // message actions
    handleCopy,
    handleEditImage,
    handleDraftEdit,
    handleDraftSchedule,
    handleDraftDelete,
    // scheduling
    handleScheduleResolve,
    handleScheduleDismiss,
    // annotation
    annotationImageUrl,
    setAnnotationImageUrl,
    handleEditComplete,
  }
}
